// What this app hears about the signed-in account, handed to whoever wants the
// latest: whose it is, the account's choices and the achievements not yet
// shown.
//
// Two answers carry it. `/api/me` is asked for by the session (on load,
// sign-in and a profile save), and `/api/me/pulse` by the bell, every thirty
// seconds and whenever the page comes back on screen (B-189). The account's
// choices ride on both (M2), so they reach this device on the bell's rhythm
// without a request of their own: this is how they get from those two places
// to `accountPrefs.tsx`, `achievements.ts` and `milestones.ts`, none of which
// should have to know about them. The pulse is those parts of `/api/me` and
// nothing else, so the bell does not read the account's every league twice a
// minute (2026-10-10); the milestone cards waiting are the pulse's alone.
//
// `askedAt` is when the request LEFT, not when it came back: an answer asked
// for before a change made on this device says nothing about that change and
// must not undo it (`accountPrefs.tsx`).

import type { FoundAchievement, MePulse, MeView, MilestoneMoment, UserPrefs } from '@fsp/domain';

/** What a listener reads of an answer. Absent: that answer did not say. */
export interface MeNews {
  userId: string;
  prefs?: UserPrefs | null;
  unlocked?: FoundAchievement[];
  /** The milestone cards waiting (H7): only the pulse says, so `/api/me` leaves it absent. */
  milestones?: MilestoneMoment[];
}

export function newsOfMe(view: MeView): MeNews {
  return { userId: view.user.id, prefs: view.prefs, unlocked: view.unlocked };
}

export function newsOfPulse(pulse: MePulse): MeNews {
  return {
    userId: pulse.userId,
    prefs: pulse.prefs,
    unlocked: pulse.unlocked,
    milestones: pulse.milestones,
  };
}

type Listener = (news: MeNews, askedAt: number) => void;

const listeners = new Set<Listener>();
let latest: { news: MeNews; askedAt: number } | null = null;

export function publishMe(news: MeNews, askedAt: number): void {
  latest = { news, askedAt };
  for (const listener of listeners) listener(news, askedAt);
}

/**
 * Hear every answer from now on, and the latest one at once if there was one,
 * so a listener mounted after the first answer is not left waiting thirty
 * seconds for the next. Returns the way to stop.
 */
export function onMe(listener: Listener): () => void {
  listeners.add(listener);
  if (latest !== null) listener(latest.news, latest.askedAt);
  return () => {
    listeners.delete(listener);
  };
}

/** Signing out: an answer about the last account is no answer about the next. */
export function forgetMe(): void {
  latest = null;
}

// ASK NOW. An action that earned the person an achievement answers with
// `x-achievements-unlocked` (A4, ACHIEVEMENTS.md §15), so the moment should
// not wait for the bell's next half minute: whoever asks on a timer (the
// bell, `/api/me/pulse`) asks now as well.
const askers = new Set<() => void>();

/** Something changed what the pulse would say: ask it now rather than on the next tick. */
export function askMeNow(): void {
  for (const ask of askers) ask();
}

/** Be the one who asks when `askMeNow` is called. Returns the way to stop. */
export function onAskMe(ask: () => void): () => void {
  askers.add(ask);
  return () => {
    askers.delete(ask);
  };
}
